import { useAuth } from "@dl/web-core-js/auth";
import { columns as defineColumns, DataTable, useServerTable } from "@dl/web-core-js/table";
import { Badge, Button, toast, useConfirm } from "@dl/web-core-js/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Undo2 } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  formatNumber,
  refusalMessage,
  STOCK_MOVEMENT_KINDS,
  StockAdjustmentReasonClient,
  StockClient,
  StockMovementClient,
  type StockMovementDTO,
  type StockMovementKind,
} from "../../api/manufacturing";
import { WarehouseClient } from "../../api/product";
import { OpenRecord } from "../../components/OpenRecord";

/**
 * The stock ledger as a grid, paged in the database — 1.4 million rows and
 * counting.
 *
 * <p>Used whole on the Stock page and pinned to one part on its detail page;
 * {@code partId} narrows every request rather than filtering what came back, so
 * a part's history is exactly as long as it is.</p>
 *
 * <p>Reversal is offered on a movement that has not been reversed and is not
 * itself a reversal — the same two rules the server and the database enforce.
 * It posts the exact opposite; the original stays, marked, so the history reads
 * as what was believed and when it stopped being believed.</p>
 */
/** A running balance: blank when unknown, red below zero. */
function balanceCell(value: number | null | undefined) {
  if (value == null) return <span />;
  return <span className={"tabular-nums " + (value < 0 ? "text-risk" : "")}>{formatNumber(value)}</span>;
}

/** A movement kind in words, not the database enum. */
export function stockMovementKindLabel(t: (key: string, fallback: string) => string, kind: StockMovementKind): string {
  return {
    RECEIPT: t("stock.kind.receipt", "Receipt"),
    ISSUE: t("stock.kind.issue", "Issue"),
    PRODUCTION_OUTPUT: t("stock.kind.production-output", "Production output"),
    SHIPMENT: t("stock.kind.shipment", "Shipment"),
    TRANSFER: t("stock.kind.transfer", "Transfer"),
    ADJUSTMENT: t("stock.kind.adjustment", "Adjustment"),
    COUNT: t("stock.kind.count", "Count"),
    OPENING: t("stock.kind.opening", "Opening balance"),
  }[kind] ?? kind;
}

export function StockMovementsGrid({
  partId,
  purchaseOrderId,
  productionOrderId,
  stockDocumentId,
  serviceOrderId,
  stateKey,
}: {
  partId?: number;
  /** Pins the grid to one purchase order's receipts. */
  purchaseOrderId?: number;
  /** Pins the grid to one production order's issues and output. */
  productionOrderId?: number;
  /** Pins the grid to one document's movements — a posted count, a transfer. */
  stockDocumentId?: number;
  /** Pins the grid to one service order's shipments. */
  serviceOrderId?: number;
  stateKey?: string;
}) {
  const { t } = useTranslation();
  const { http, canWrite } = useAuth();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const movements = useMemo(() => new StockMovementClient(http), [http]);
  const stock = useMemo(() => new StockClient(http), [http]);

  // The warehouse and reason filters need their options declared: a loaded
  // page is no sample of 1.4 million rows.
  const warehouses = useQuery({
    queryKey: ["warehouse", "all"],
    queryFn: () => new WarehouseClient(http).findAll(),
    staleTime: 30 * 60_000,
  });
  const reasons = useQuery({
    queryKey: ["stock-adjustment-reason", "all"],
    queryFn: () => new StockAdjustmentReasonClient(http).findAll(),
    staleTime: 30 * 60_000,
  });

  // Pinning narrows every request rather than filtering what came back.
  const pinned: Record<string, string[]> = {
    ...(partId === undefined ? {} : { part_id: [String(partId)] }),
    ...(purchaseOrderId === undefined ? {} : { purchase_order_id: [String(purchaseOrderId)] }),
    ...(productionOrderId === undefined ? {} : { production_order_id: [String(productionOrderId)] }),
    ...(stockDocumentId === undefined ? {} : { stock_document_id: [String(stockDocumentId)] }),
    ...(serviceOrderId === undefined ? {} : { service_order_id: [String(serviceOrderId)] }),
  };
  const isPinned = Object.keys(pinned).length > 0;
  const ledger = useServerTable<StockMovementDTO>({
    queryKey: ["manufacturing", "stock-movement", partId ?? "all", purchaseOrderId ?? "all", productionOrderId ?? "all", stockDocumentId ?? "all", serviceOrderId ?? "all"],
    fetchPage: (request) => movements.find({ ...request, filters: { ...(request.filters ?? {}), ...pinned } }),
    fetchTotal: isPinned ? undefined : () => movements.count(),
  });

  const reverse = async (row: StockMovementDTO) => {
    const ok = await confirm({
      title: t("stock.reverse.title", "Reverse this movement?"),
      message: t(
        "stock.reverse.message",
        "Posts {{quantity}} of {{part}} at {{warehouse}} to cancel it exactly. The original stays in the ledger, marked as reversed.",
        { quantity: formatNumber(-row.quantity), part: row.part_number, warehouse: row.warehouse_code },
      ),
      confirmLabel: t("stock.reverse.confirm", "Reverse"),
    });
    if (!ok) return;
    try {
      await stock.reverse(row.id, null);
      await queryClient.invalidateQueries({ queryKey: ["manufacturing", "stock-movement"] });
      await queryClient.invalidateQueries({ queryKey: ["manufacturing", "stock-balance"] });
      await queryClient.invalidateQueries({ queryKey: ["manufacturing", "part-grid"] });
      await queryClient.invalidateQueries({ queryKey: ["purchasing"] });
      // The part page's header and the picker both show on-hand too.
      await queryClient.invalidateQueries({ queryKey: ["manufacturing", "part"] });
      await queryClient.invalidateQueries({ queryKey: ["manufacturing", "part-picker"] });
      toast.success(t("stock.reverse.done", "Movement reversed"));
    } catch (failure) {
      toast.error(refusalMessage(failure, t("error.generic", "Something went wrong.")));
    }
  };

  const cols = useMemo(() => {
    const c = defineColumns<StockMovementDTO>();
    const reasonName = new Map((reasons.data ?? []).map((reason) => [reason.code, reason.name]));
    const all = [
      c.text("movement_date", (row) => row.movement_date, { headerKey: "column.stock.date", filter: "daterange", priority: 3 }),
      c.text("part_number", (row) => row.part_number, { headerKey: "column.part.number", priority: 1 }),
      c.text("warehouse_code", (row) => row.warehouse_code, {
        headerKey: "column.stock.warehouse",
        filter: "select",
        filterOptions: [...(warehouses.data ?? [])]
          .sort((a, b) => a.code.localeCompare(b.code))
          .map((w) => ({ value: w.code, label: w.code })),
        priority: 3,
      }),
      c.custom(
        "kind",
        (row) => row.kind,
        (row) => (
          <span className="inline-flex items-center gap-1.5">
            <Badge
              size="sm"
              variant={
                row.kind === "ADJUSTMENT" || row.kind === "COUNT"
                  ? "warning"
                  : row.kind === "OPENING"
                    ? "neutral"
                    : "info"
              }
            >
              {stockMovementKindLabel(t, row.kind)}
            </Badge>
            {row.reverses_movement_id !== null && (
              <span className="text-[11px] text-ink-muted">
                {t("stock.reversal-of", "reverses #{{id}}", { id: row.reverses_movement_id })}
              </span>
            )}
            {row.reversed_by_movement_id !== null && (
              <span className="text-[11px] text-risk">
                {t("stock.reversed-by", "reversed by #{{id}}", { id: row.reversed_by_movement_id })}
              </span>
            )}
          </span>
        ),
        {
          headerKey: "column.stock.kind",
          filter: "multiselect",
          filterOptions: STOCK_MOVEMENT_KINDS.map((kind) => ({ value: kind, label: stockMovementKindLabel(t, kind) })),
          priority: 2,
        },
      ),
      c.custom(
        "quantity",
        (row) => row.quantity,
        (row) => (
          <span className={"tabular-nums " + (row.quantity > 0 ? "text-ok" : row.quantity < 0 ? "text-risk" : "text-ink-faint")}>
            {row.quantity > 0 ? "+" : ""}
            {formatNumber(row.quantity)}
          </span>
        ),
        { headerKey: "column.stock.quantity", filter: "range", align: "right", priority: 2 },
      ),
      // Where the part stood in that movement's own warehouse, once posted. The
      // server computes it per row, so it can be neither filtered nor sorted.
      c.custom(
        "balance_after",
        (row) => row.balance_after ?? "",
        (row) => balanceCell(row.balance_after),
        {
          header: t("column.stock.after", "After, in warehouse"),
          filter: "none",
          enableSorting: false,
          align: "right",
          priority: 3,
        },
      ),
      c.custom(
        "balance_before",
        (row) => (row.balance_after == null ? "" : row.balance_after - row.quantity),
        (row) => balanceCell(row.balance_after == null ? null : row.balance_after - row.quantity),
        {
          header: t("column.stock.before", "Before, in warehouse"),
          filter: "none",
          enableSorting: false,
          align: "right",
          defaultVisible: false,
        },
      ),
      // The reason's name, not its code; a code no longer on file still shows.
      c.text("reason_code", (row) => (row.reason_code ? (reasonName.get(row.reason_code) ?? row.reason_code) : ""), {
        headerKey: "column.stock.reason",
        filter: "select",
        filterOptions: (reasons.data ?? []).map((reason) => ({ value: reason.code, label: reason.name })),
      }),
      c.custom(
        "source",
        (row) => row.source,
        (row) =>
          row.source === "SIGM" ? (
            <span className="text-[11px] text-ink-faint">SIGM</span>
          ) : (
            <Badge size="sm" variant="neutral">
              nexpim
            </Badge>
          ),
        {
          headerKey: "column.stock.source",
          filter: "select",
          filterOptions: [
            { value: "NEXPIM", label: "nexpim" },
            { value: "SIGM", label: "SIGM" },
          ],
        },
      ),
      c.text("sigm_reference", (row) => row.sigm_reference ?? row.note ?? "", {
        headerKey: "column.stock.reference",
        filter: "none",
        enableSorting: false,
      }),
      c.number("total_cost", (row) => row.total_cost, { headerKey: "column.stock.cost", filter: "range", defaultVisible: false }),
      c.number("id", (row) => row.id, { headerKey: "column.stock.id", filter: "none", defaultVisible: false }),
    ];
    // Pinned to one part, the part column says nothing.
    return partId === undefined ? all : all.filter((column) => column.id !== "part_number");
  }, [t, partId, warehouses.data, reasons.data]);

  return (
    <DataTable<StockMovementDTO>
      data={ledger.rows}
      columns={cols}
      getRowId={(row) => String(row.id)}
      stateKey={stateKey}
      loading={ledger.loading}
      server={ledger.mode}
      initialSorting={[{ id: "movement_date", desc: true }]}
      rowActions={(row) => (
        <>
          {canWrite && row.reverses_movement_id === null && row.reversed_by_movement_id === null ? (
            <Button variant="ghost" size="sm" onClick={() => void reverse(row)} aria-label={t("stock.reverse.confirm", "Reverse")}>
              <Undo2 className="size-4" />
            </Button>
          ) : null}
          {partId === undefined && (
            <OpenRecord target={{ kind: "part", id: row.part_id, tab: "stock" }} label={row.part_number ?? ""} />
          )}
        </>
      )}
      emptyMessage={t("stock.movements.empty", "No movements.")}
    />
  );
}
